// «الصيانة» — one branch (GET /maintenance/branches/{bucket}): its KPIs, parts cost per Riyadh
// month, technicians, repeat failures and its maintenance locations.
import { useEffect, useState } from "react";
import { Repeat } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorText } from "@/lib/errorText";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { InlineLoader } from "@/components/common/Loader";
import { MAINT } from "@/constants/testIds";
import { MaintKpis } from "./MaintKpis";
import { TechniciansCard } from "./BranchTable";
import { bucketName, fmtSar, fmtWhen } from "./maintText";

const head = "text-xs font-bold text-[color:var(--ink-muted)]";

/** `row` = an overview row (or null); `range` = {from, to}. */
export function BranchSheet({ row, range, onClose, onOpenItem, onShowItems, t, lang }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        setData(null);
        if (!row) return undefined;
        let alive = true;
        api.get(`/maintenance/branches/${encodeURIComponent(row.bucket)}`, { params: { from: range.from, to: range.to } })
            .then((r) => { if (alive) setData(r.data); })
            .catch((e) => { if (alive) { toast.error(apiErrorText(e)); onClose(); } });
        return () => { alive = false; };
    }, [row?.bucket, range.from, range.to]); // eslint-disable-line react-hooks/exhaustive-deps

    const max = Math.max(1, ...((data?.parts_cost_by_month || []).map((m) => m.total || 0)));
    return (
        <Sheet open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
            <SheetContent side="right" data-testid={MAINT.branchSheet} className="w-full overflow-y-auto bg-[color:var(--bg-surface)] p-0 sm:max-w-2xl">
                <SheetHeader className="sticky top-0 z-10 border-b border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] px-4 py-3 pr-12 text-start sm:text-start">
                    <SheetTitle className="text-base font-extrabold text-[color:var(--ink)]"><bdi>{bucketName(row, lang)}</bdi></SheetTitle>
                    <SheetDescription className="text-xs text-[color:var(--ink-muted)]">
                        <span dir="ltr">{range.from}</span><span> → </span><span dir="ltr">{range.to}</span>
                    </SheetDescription>
                </SheetHeader>
                {!data ? <InlineLoader /> : (
                    <div className="space-y-5 p-4">
                        <MaintKpis network={data.kpis} t={t} lang={lang} compact />
                        <section data-testid={MAINT.branchMonths} className="space-y-2">
                            <h4 className={head}>{t("mt_branch_months")}</h4>
                            <ul className="space-y-1.5">
                                {data.parts_cost_by_month.map((m) => (
                                    <li key={m.month} className="grid grid-cols-[4.5rem_1fr_auto] items-center gap-2 text-xs">
                                        <span dir="ltr" className="tnum text-[color:var(--ink-muted)]">{m.month}</span>
                                        <span className="h-2 overflow-hidden rounded-full bg-[color:var(--bg-surface-2)]">
                                            <span className="block h-full rounded-full bg-[color:var(--brand-orange)]" style={{ width: `${(100 * (m.total || 0)) / max}%` }} />
                                        </span>
                                        <span className="tnum font-semibold text-[color:var(--ink)]">{fmtSar(m.total, lang, t)}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                        <TechniciansCard rows={data.technicians} t={t} lang={lang} />
                        <section data-testid={MAINT.branchRepeats} className="space-y-2">
                            <h4 className={head}>{t("mt_branch_repeats")}</h4>
                            {!data.repeat_failures.length ? <p className="text-xs text-[color:var(--ink-muted)]">{t("mt_no_repeats")}</p> : (
                                <ul className="space-y-1.5">
                                    {data.repeat_failures.map((w) => (
                                        <li key={w.id}>
                                            <button type="button" onClick={() => onOpenItem(w)}
                                                    className="flex w-full items-center justify-between gap-2 rounded-[var(--radius-md)] border border-[color:var(--border-soft)] px-3 py-2 text-start text-xs hover:bg-[color:var(--brand-orange-soft)]">
                                                <span className="flex min-w-0 items-center gap-1.5">
                                                    <Repeat className="h-3.5 w-3.5 shrink-0 text-[color:var(--brand-brown)]" />
                                                    <span className="truncate font-semibold text-[color:var(--ink)]"><span dir="ltr">{w.code}</span> · <bdi>{w.title}</bdi></span>
                                                </span>
                                                <span className="shrink-0 text-[10px] text-[color:var(--ink-faint)]">{fmtWhen(w.created_at, lang)}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                        <section className="space-y-2">
                            <h4 className={head}>{t("mt_branch_locations")}</h4>
                            <div className="flex flex-wrap gap-1.5">
                                {data.locations.map((l) => (
                                    <Badge key={l.code} variant={l.state === "confirmed" ? "secondary" : l.state === "suggested" ? "warning" : "outline"}>
                                        <bdi>{lang === "ar" ? l.name_ar || l.name_en : l.name_en || l.name_ar}</bdi>
                                        <span dir="ltr" className="text-[color:var(--ink-faint)]">{l.code}</span>
                                        {l.state === "suggested" ? <span>· {t("mt_suggested")}</span> : null}
                                    </Badge>
                                ))}
                            </div>
                        </section>
                        <button type="button" data-testid={MAINT.branchShowItems} onClick={() => onShowItems(row)}
                                className="h-11 w-full rounded-full border border-[color:var(--border-soft)] text-sm font-bold text-[color:var(--brand-brown)] transition-[background-color] duration-150 hover:bg-[color:var(--brand-orange-soft)]">
                            {t("mt_branch_show_items")}
                        </button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
